import { useMemo, useState } from 'react'
import {
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronRight,
  Circle,
  Copy,
  Hourglass,
  ShieldAlert,
  Sparkles,
  X,
} from 'lucide-react'
import { tokens } from '@/theme'
import type {
  GateAssessment,
  GateCode,
  GateCriterionReadiness,
  GateOutcome,
  GateReadiness,
  GateReadinessResponse,
} from '@/domain/types'
import { useClearGate, type ClearGateBody } from '@/api/hooks/useGates'
import { useLookupBlockedReasons } from '@/api/hooks/useLookups'
import { useLookupUsers } from '@/api/hooks/useLookupUsers'
import { ErrorPanel } from '@/components/feedback'

interface Props {
  initiativeId: number
  readiness: GateReadinessResponse
  /** Bumped by the detail view to re-fetch after a clear lands. */
  onCleared: () => void
}

/**
 * The gate panel.
 *
 * Renders exactly what `GET /api/initiatives/{id}/gates` returns and
 * computes no readiness of its own. That is the whole point of having one
 * endpoint: the board, this panel and the agent cannot disagree about
 * whether a gate is clearable, because none of them decides.
 *
 * Only the pending gate is shown. Passed gates and gates still ahead are
 * still in the payload — the decision block below reads the inbound one for
 * a Step back — but they are not rendered: the page is about what has to
 * happen next, and five collapsed rows to find the one that matters was the
 * cost of showing the whole ladder. What was attested at an earlier gate,
 * and by whom, is on the activity feed.
 */
export function GatePanel({ initiativeId, readiness, onCleared }: Props) {
  const currentGate = readiness.data.find(g => g.isCurrentGate) ?? null
  const inboundGate = readiness.data.find(g => g.toStageCode === readiness.currentStageCode) ?? null

  return (
    <section style={{ marginTop: 32 }}>
      <SectionHeader
        label="Gates"
        trailing={
          readiness.overrideCount > 0 ? (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 11,
                fontFamily: tokens.fontDisplay,
                fontWeight: tokens.weightDemi,
                color: tokens.statusRed,
                background: tokens.statusRedWash,
                border: `1px solid ${tokens.statusRedLine}`,
                borderRadius: tokens.radiusSm,
                padding: '2px 8px',
              }}
            >
              <ShieldAlert size={11} strokeWidth={1.8} />
              {readiness.overrideCount} override{readiness.overrideCount === 1 ? '' : 's'}
            </span>
          ) : null
        }
      />

      {currentGate && (
        <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 10 }}>
          <GateRow key={currentGate.gateCode} gate={currentGate} />
        </ol>
      )}

      <GateDecision
        initiativeId={initiativeId}
        currentGate={currentGate}
        inboundGate={inboundGate}
        onCleared={onCleared}
      />
    </section>
  )
}

// ---------------------------------------------------------------------------
// One gate
// ---------------------------------------------------------------------------

function GateRow({ gate }: { gate: GateReadiness }) {
  // Open the gate the initiative is standing in front of; leave the rest
  // collapsed so the panel reads as a summary until you ask for detail.
  const [expanded, setExpanded] = useState(gate.isCurrentGate)

  const tone = gate.isPassed
    ? { fg: tokens.statusGreen, bg: tokens.statusGreenWash, border: tokens.statusGreenLine }
    : gate.isCurrentGate && gate.ready
      ? { fg: tokens.statusTeal, bg: tokens.statusTealWash, border: tokens.statusTealLine }
      : gate.isCurrentGate
        ? { fg: tokens.statusAmber, bg: tokens.statusAmberWash, border: tokens.statusAmberLine }
        : { fg: tokens.ink3, bg: tokens.paper2, border: tokens.line }

  return (
    <li
      style={{
        background: tokens.paper,
        border: `1px solid ${gate.isCurrentGate ? tone.border : tokens.line}`,
        borderRadius: tokens.radiusMd,
        fontFamily: tokens.fontDisplay,
      }}
    >
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        aria-expanded={expanded}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          width: '100%',
          background: 'transparent',
          border: 'none',
          padding: '12px 16px',
          cursor: 'pointer',
          textAlign: 'left',
          fontFamily: 'inherit',
          flexWrap: 'wrap',
        }}
      >
        {expanded ? (
          <ChevronDown size={13} strokeWidth={1.8} color={tokens.ink3} />
        ) : (
          <ChevronRight size={13} strokeWidth={1.8} color={tokens.ink3} />
        )}
        <span
          style={{
            fontSize: 11,
            fontWeight: tokens.weightDemi,
            letterSpacing: '0.14em',
            color: tone.fg,
            background: tone.bg,
            border: `1px solid ${tone.border}`,
            borderRadius: tokens.radiusSm,
            padding: '2px 8px',
          }}
        >
          {gate.gateCode}
        </span>
        <span style={{ fontSize: 14, fontWeight: tokens.weightDemi, color: tokens.ink }}>
          {gate.gateName}
        </span>
        <span style={{ fontSize: 12, color: tokens.ink4 }}>
          {gate.fromStageCode} → {gate.toStageCode}
        </span>
        <span style={{ flex: 1 }} />
        <GateStatusChip gate={gate} />
      </button>

      {expanded && (
        <div style={{ padding: '0 16px 14px', borderTop: `1px solid ${tokens.line}` }}>
          {gate.assessment && <AssessmentBanner assessment={gate.assessment} />}

          <ul
            style={{ listStyle: 'none', margin: '12px 0 0', padding: 0, display: 'grid', gap: 8 }}
          >
            {gate.criteria.map(criterion => (
              <CriterionRow key={criterion.criterionKey} criterion={criterion} />
            ))}
          </ul>

          <div
            style={{
              marginTop: 12,
              paddingTop: 10,
              borderTop: `1px dashed ${tokens.line}`,
              fontSize: 11.5,
              color: tokens.ink3,
              display: 'grid',
              gap: 4,
            }}
          >
            <span>Checklist v{gate.checklistVersion || '—'}</span>
            <span>{gate.authorityNote}</span>
            {gate.lastClear && (
              <span>
                Last outcome: <strong>{gate.lastClear.outcome}</strong> by{' '}
                {gate.lastClear.clearedByDisplayName || '—'} on{' '}
                {new Date(gate.lastClear.clearedAt).toLocaleDateString()}
                {gate.lastClear.overridden ? ' · with override' : ''}
              </span>
            )}
          </div>
        </div>
      )}
    </li>
  )
}

/**
 * What the assessor has to say about this gate.
 *
 * This exists because of a specific failure the decision document called
 * out: once Gate 0 is machine-evaluated, an initiative sitting at Idea with
 * three unmet hard criteria could mean four different things — the model
 * scored it low, the model has not run, the model errored, or the
 * description was edited and the good score no longer applies. Rendered as
 * a bare list of unmet criterion keys they are indistinguishable, and only
 * one of the four is the reader's fault.
 *
 * So the banner leads with the state, and only shows scores when there are
 * scores that describe the content currently on screen.
 */
function AssessmentBanner({ assessment }: { assessment: GateAssessment }) {
  const flaggedDuplicate = assessment.duplicateState === 'NeedsReview'

  const tone = (() => {
    switch (assessment.state) {
      case 'Pending':
      case 'Stale':
        return { fg: tokens.ink3, bg: tokens.paper2, border: tokens.line }
      case 'Failed':
        return { fg: tokens.statusRed, bg: tokens.statusRedWash, border: tokens.statusRedLine }
      case 'Complete':
        return flaggedDuplicate
          ? { fg: tokens.statusAmber, bg: tokens.statusAmberWash, border: tokens.statusAmberLine }
          : { fg: tokens.ink3, bg: tokens.paper2, border: tokens.line }
    }
  })()

  const { icon, headline, detail } = (() => {
    switch (assessment.state) {
      case 'Pending':
        return {
          icon: <Hourglass size={13} strokeWidth={1.8} color={tone.fg} />,
          headline: 'Assessment pending',
          detail:
            'Orion has not scored this initiative yet. It is queued — nothing is wrong with what you have written.',
        }
      case 'Stale':
        return {
          icon: <Hourglass size={13} strokeWidth={1.8} color={tone.fg} />,
          headline: 'Re-assessment pending',
          detail:
            'This initiative was edited after it was last scored. Orion is re-reading it; the previous scores no longer apply.',
        }
      case 'Failed':
        return {
          icon: <AlertTriangle size={13} strokeWidth={1.8} color={tone.fg} />,
          headline: 'Assessment failed',
          detail:
            assessment.failureReason ??
            'Orion could not complete the assessment. This is a system fault, not a judgement on the initiative.',
        }
      case 'Complete':
        return {
          icon: flaggedDuplicate ? (
            <Copy size={13} strokeWidth={1.8} color={tone.fg} />
          ) : (
            <Sparkles size={13} strokeWidth={1.8} color={tone.fg} />
          ),
          headline: flaggedDuplicate ? 'Possible duplicate — needs review' : 'Assessed by Orion',
          detail: assessment.rationale,
        }
    }
  })()

  return (
    <div
      style={{
        marginTop: 12,
        padding: '10px 12px',
        background: tone.bg,
        border: `1px solid ${tone.border}`,
        borderRadius: tokens.radiusSm,
        display: 'grid',
        gap: 6,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {icon}
        <span style={{ fontSize: 12, fontWeight: tokens.weightDemi, color: tone.fg }}>
          {headline}
        </span>
      </div>

      {detail && (
        <p style={{ margin: 0, fontSize: 11.5, lineHeight: 1.5, color: tokens.ink3 }}>{detail}</p>
      )}

      {flaggedDuplicate && assessment.duplicateTopInitiativeId !== null && (
        <p style={{ margin: 0, fontSize: 11.5, lineHeight: 1.5, color: tokens.ink3 }}>
          Closest match: initiative #{assessment.duplicateTopInitiativeId} (
          {assessment.duplicateTopScore}/100). Nothing has been closed and no decision has been
          taken — a reviewer who judges this to be distinct work can clear Gate 0 with a
          hard-criterion override.
        </p>
      )}

      {assessment.state === 'Complete' && (
        <dl
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '4px 16px',
            margin: '2px 0 0',
            fontSize: 11,
            color: tokens.ink4,
          }}
        >
          <ScoreStat
            label="Problem clarity"
            score={assessment.problemClarityScore}
            // A floor: at or above it passes.
            threshold={`≥ ${assessment.thresholds.problemClarityMin}`}
            passed={
              assessment.problemClarityScore !== null &&
              assessment.problemClarityScore >= assessment.thresholds.problemClarityMin
            }
          />
          <ScoreStat
            label="AI plausibility"
            score={assessment.aiPlausibilityScore}
            threshold={`≥ ${assessment.thresholds.aiPlausibilityMin}`}
            passed={
              assessment.aiPlausibilityScore !== null &&
              assessment.aiPlausibilityScore >= assessment.thresholds.aiPlausibilityMin
            }
          />
          <ScoreStat
            label="Duplicate risk"
            score={assessment.duplicateTopScore}
            // A ceiling, and strict: 40 flags, 39 does not.
            threshold={`< ${assessment.thresholds.duplicateMax}`}
            passed={
              assessment.duplicateTopScore !== null &&
              assessment.duplicateTopScore < assessment.thresholds.duplicateMax
            }
          />
        </dl>
      )}

      {assessment.assessedAt && (
        <span style={{ fontSize: 10.5, color: tokens.ink4 }}>
          {new Date(assessment.assessedAt).toLocaleString()}
          {assessment.modelDeployment ? ` · ${assessment.modelDeployment}` : ''}
          {assessment.promptVersion ? ` · ${assessment.promptVersion}` : ''}
        </span>
      )}
    </div>
  )
}

/**
 * One score against its threshold.
 *
 * The threshold is always shown next to the number. "62" invites an appeal;
 * "62, needs ≥ 70" tells you what to do, and it is the same information the
 * predicate used, shipped by the API rather than restated here.
 */
function ScoreStat({
  label,
  score,
  threshold,
  passed,
}: {
  label: string
  score: number | null
  threshold: string
  passed: boolean
}) {
  // dt/dd are the only elements allowed inside a dl's div wrapper, so the
  // threshold rides inside the dd rather than sitting beside it.
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
      <dt style={{ margin: 0 }}>{label}</dt>
      <dd style={{ margin: 0, display: 'flex', alignItems: 'baseline', gap: 5 }}>
        <span
          style={{
            fontWeight: tokens.weightDemi,
            color: passed ? tokens.statusGreen : tokens.statusAmber,
          }}
        >
          {score ?? '—'}
        </span>
        <span>({threshold})</span>
      </dd>
    </div>
  )
}

function GateStatusChip({ gate }: { gate: GateReadiness }) {
  // On an assessed gate, "3 unmet" is misleading while the assessor is
  // still working: the criteria are unmet because nothing has scored them,
  // not because the initiative fell short. Say which it is.
  const awaitingAssessment =
    gate.assessment !== null &&
    (gate.assessment.state === 'Pending' || gate.assessment.state === 'Stale')
  const assessmentFailed = gate.assessment?.state === 'Failed'

  const label = gate.isPassed
    ? 'Passed'
    : gate.isCurrentGate
      ? gate.ready
        ? 'Ready'
        : awaitingAssessment
          ? 'Assessing'
          : assessmentFailed
            ? 'Assessment failed'
            : `${gate.unmetHardCriteria.length} unmet`
      : 'Ahead'

  const colour = gate.isPassed
    ? tokens.statusGreen
    : gate.isCurrentGate
      ? gate.ready
        ? tokens.statusTeal
        : assessmentFailed
          ? tokens.statusRed
          : awaitingAssessment
            ? tokens.ink3
            : tokens.statusAmber
      : tokens.ink4

  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: tokens.weightDemi,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        color: colour,
      }}
    >
      {label}
    </span>
  )
}

function CriterionRow({ criterion }: { criterion: GateCriterionReadiness }) {
  const icon = criterion.met ? (
    <Check size={13} strokeWidth={2.2} color={tokens.statusGreen} />
  ) : criterion.advisory ? (
    <Circle size={11} strokeWidth={1.8} color={tokens.ink4} />
  ) : (
    <X size={13} strokeWidth={2.2} color={tokens.statusAmber} />
  )

  return (
    <li style={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: 10, fontSize: 13 }}>
      <span style={{ paddingTop: 2 }}>{icon}</span>
      <span style={{ color: criterion.met ? tokens.ink2 : tokens.ink, lineHeight: 1.45 }}>
        {criterion.criterionText}
        {criterion.recorded?.overridden && (
          <span style={{ color: tokens.statusRed, fontWeight: tokens.weightDemi }}>
            {' '}
            · overridden
          </span>
        )}
      </span>
      <span
        style={{
          fontSize: 9.5,
          fontWeight: tokens.weightDemi,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: tokens.ink4,
          whiteSpace: 'nowrap',
          paddingTop: 3,
        }}
      >
        {criterion.kind}
      </span>
    </li>
  )
}

// ---------------------------------------------------------------------------
// Decision block
// ---------------------------------------------------------------------------

const OUTCOME_LABELS: Record<GateOutcome, string> = {
  Advance: 'Advance',
  StepBack: 'Step back',
  Park: 'Park',
  Close: 'Close',
  Salvage: 'Salvage',
}

const OUTCOME_HELP: Record<GateOutcome, string> = {
  Advance: 'Move forward one stage. Every hard criterion must be met or overridden.',
  StepBack: 'Move back one stage, with a reason. Recorded on the gate log.',
  Park: 'Stay where it is, marked blocked. The reason feeds the board’s blocked aggregate.',
  Close: 'Stop the work. The initiative stays on the board, in the stage where it died.',
  Salvage: 'Stop the work but keep something. Record the harvested asset and where it now lives.',
}

/**
 * One decision block rather than a set of buttons per gate row.
 *
 * The gate a decision applies to is not the user's choice — it follows from
 * the outcome. Advance, Park, Close and Salvage act on the gate in front of
 * the initiative; Step back acts on the gate it came through. Making people
 * pick both would let them pick an illegal pair and get a 400 for it.
 */
function GateDecision({
  initiativeId,
  currentGate,
  inboundGate,
  onCleared,
}: {
  initiativeId: number
  currentGate: GateReadiness | null
  inboundGate: GateReadiness | null
  onCleared: () => void
}) {
  const [outcome, setOutcome] = useState<GateOutcome | null>(null)

  const available = useMemo<GateOutcome[]>(() => {
    const list: GateOutcome[] = []
    if (currentGate) list.push('Advance')
    if (inboundGate) list.push('StepBack')
    if (currentGate ?? inboundGate) list.push('Park', 'Close', 'Salvage')
    return list
  }, [currentGate, inboundGate])

  const gateFor = (o: GateOutcome): GateReadiness | null =>
    o === 'StepBack' ? inboundGate : (currentGate ?? inboundGate)

  if (available.length === 0) {
    return (
      <p
        style={{
          marginTop: 16,
          fontSize: 13,
          color: tokens.ink3,
          fontFamily: tokens.fontDisplay,
        }}
      >
        No gate applies at this stage.
      </p>
    )
  }

  const selectedGate = outcome ? gateFor(outcome) : null

  return (
    <div
      style={{
        marginTop: 20,
        padding: '18px 20px',
        background: tokens.paper2,
        border: `1px solid ${tokens.line}`,
        borderRadius: tokens.radiusMd,
        fontFamily: tokens.fontDisplay,
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: tokens.weightDemi,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: tokens.ink3,
          marginBottom: 12,
        }}
      >
        Gate decision
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {available.map(o => {
          const gate = gateFor(o)
          const disabled = !gate?.authorised
          const active = outcome === o
          return (
            <button
              key={o}
              type="button"
              disabled={disabled}
              title={disabled ? (gate?.authorityNote ?? 'Not available') : OUTCOME_HELP[o]}
              onClick={() => setOutcome(active ? null : o)}
              style={{
                background: active ? tokens.gold : 'transparent',
                color: active ? tokens.paper : disabled ? tokens.ink4 : tokens.ink2,
                border: `1px solid ${active ? tokens.gold : tokens.line2}`,
                borderRadius: tokens.radiusMd,
                padding: '8px 16px',
                fontSize: 12,
                fontFamily: tokens.fontDisplay,
                fontWeight: tokens.weightDemi,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.5 : 1,
              }}
            >
              {OUTCOME_LABELS[o]}
            </button>
          )
        })}
      </div>

      {outcome && selectedGate && (
        <ClearForm
          key={`${outcome}-${selectedGate.gateCode}`}
          initiativeId={initiativeId}
          gate={selectedGate}
          outcome={outcome}
          onDone={() => {
            setOutcome(null)
            onCleared()
          }}
          onCancel={() => setOutcome(null)}
        />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Clear form
// ---------------------------------------------------------------------------

function ClearForm({
  initiativeId,
  gate,
  outcome,
  onDone,
  onCancel,
}: {
  initiativeId: number
  gate: GateReadiness
  outcome: GateOutcome
  onDone: () => void
  onCancel: () => void
}) {
  const { state, clear } = useClearGate()
  const blockedReasons = useLookupBlockedReasons()
  const users = useLookupUsers()

  const [reason, setReason] = useState('')
  const [attested, setAttested] = useState<Set<string>>(new Set())
  const [overridden, setOverridden] = useState<Set<string>>(new Set())
  const [overrideReason, setOverrideReason] = useState('')
  const [overrideApprover, setOverrideApprover] = useState('')
  const [closureReason, setClosureReason] = useState('')
  const [closureNote, setClosureNote] = useState('')
  const [blockedReasonCode, setBlockedReasonCode] = useState('')
  const [blockedNote, setBlockedNote] = useState('')
  const [confirming, setConfirming] = useState(false)

  const attestCriteria = gate.criteria.filter(c => c.attestationRequired)
  const advisoryCriteria = gate.criteria.filter(c => c.advisory)
  const unmetHard = gate.criteria.filter(c => c.kind === 'Hard' && !c.met)

  const isAdvance = outcome === 'Advance'
  const closes = outcome === 'Close' || outcome === 'Salvage'
  const busy = state.status === 'loading'

  const toggle = (set: Set<string>, key: string): Set<string> => {
    const next = new Set(set)
    if (next.has(key)) next.delete(key)
    else next.add(key)
    return next
  }

  const blockingProblems: string[] = []
  if (!isAdvance && reason.trim() === '') blockingProblems.push('A reason is required.')
  if (isAdvance) {
    const missing = attestCriteria.filter(c => !attested.has(c.criterionKey))
    if (missing.length > 0) {
      blockingProblems.push(
        `${missing.length} attestation${missing.length === 1 ? '' : 's'} still to tick.`,
      )
    }
    const stillUnmet = unmetHard.filter(c => !overridden.has(c.criterionKey))
    if (stillUnmet.length > 0) {
      blockingProblems.push(
        `${stillUnmet.length} hard criterion${stillUnmet.length === 1 ? '' : 's'} unmet.`,
      )
    }
  }
  if (overridden.size > 0 && (overrideReason.trim() === '' || overrideApprover === '')) {
    blockingProblems.push('An override needs a written reason and a named approver.')
  }
  if (closes && closureReason.trim() === '') blockingProblems.push('A closure reason is required.')
  if (outcome === 'Salvage' && closureNote.trim() === '') {
    blockingProblems.push('Salvage needs a note recording the harvested asset.')
  }
  if (outcome === 'Park' && blockedReasonCode === '') {
    blockingProblems.push('Park needs a blocked reason.')
  }

  const submit = async (): Promise<void> => {
    const body: ClearGateBody = { outcome }
    if (reason.trim()) body.reason = reason.trim()
    const attestations = [...attested]
    if (attestations.length > 0) body.attestations = attestations
    if (overridden.size > 0) {
      body.overrides = [...overridden].map(criterionKey => ({ criterionKey }))
      body.overrideReason = overrideReason.trim()
      body.overrideApproverObjectId = overrideApprover
    }
    if (closureReason.trim()) body.closureReason = closureReason.trim()
    if (closureNote.trim()) body.closureNote = closureNote.trim()
    if (blockedReasonCode) body.blockedReasonCode = blockedReasonCode
    if (blockedNote.trim()) body.blockedNote = blockedNote.trim()

    const result = await clear(initiativeId, gate.gateCode as GateCode, body)
    if (result) onDone()
  }

  return (
    <div style={{ marginTop: 16, display: 'grid', gap: 14 }}>
      <p style={{ margin: 0, fontSize: 13, color: tokens.ink2, lineHeight: 1.5 }}>
        {OUTCOME_HELP[outcome]}{' '}
        <span style={{ color: tokens.ink3 }}>
          Recorded against {gate.gateCode} v{gate.checklistVersion || '—'}.
        </span>
      </p>

      {!isAdvance && (
        <Labelled label="Reason" required>
          <textarea
            value={reason}
            onChange={e => setReason(e.currentTarget.value)}
            rows={2}
            style={textareaStyle}
          />
        </Labelled>
      )}

      {isAdvance && attestCriteria.length > 0 && (
        <fieldset style={fieldsetStyle}>
          <legend style={legendStyle}>Attestations</legend>
          {attestCriteria.map(c => (
            <CheckRow
              key={c.criterionKey}
              checked={attested.has(c.criterionKey)}
              onChange={() => setAttested(prev => toggle(prev, c.criterionKey))}
              label={c.criterionText}
            />
          ))}
        </fieldset>
      )}

      {isAdvance && advisoryCriteria.length > 0 && (
        <fieldset style={fieldsetStyle}>
          <legend style={legendStyle}>Advisory — never blocks</legend>
          {advisoryCriteria.map(c => (
            <CheckRow
              key={c.criterionKey}
              checked={attested.has(c.criterionKey)}
              onChange={() => setAttested(prev => toggle(prev, c.criterionKey))}
              label={c.criterionText}
            />
          ))}
        </fieldset>
      )}

      {isAdvance && unmetHard.length > 0 && (
        <fieldset style={{ ...fieldsetStyle, borderColor: tokens.statusRedLine }}>
          <legend style={{ ...legendStyle, color: tokens.statusRed }}>
            Override an unmet hard criterion
          </legend>
          <p style={{ margin: '0 0 8px', fontSize: 12, color: tokens.ink3, lineHeight: 1.5 }}>
            Overrides are meant to be rare by being loud, not by being impossible. Each one is
            recorded on the append-only gate log and counted against this business unit on the
            board.
          </p>
          {unmetHard.map(c => (
            <CheckRow
              key={c.criterionKey}
              checked={overridden.has(c.criterionKey)}
              onChange={() => setOverridden(prev => toggle(prev, c.criterionKey))}
              label={c.criterionText}
            />
          ))}
          {overridden.size > 0 && (
            <div style={{ display: 'grid', gap: 10, marginTop: 10 }}>
              <Labelled label="Override reason" required>
                <textarea
                  value={overrideReason}
                  onChange={e => setOverrideReason(e.currentTarget.value)}
                  rows={2}
                  style={textareaStyle}
                />
              </Labelled>
              <Labelled label="Override approver" required>
                <select
                  value={overrideApprover}
                  onChange={e => setOverrideApprover(e.currentTarget.value)}
                  style={inputStyle}
                >
                  <option value="">Select…</option>
                  {(users.status === 'success' ? users.data.data : []).map(u => (
                    <option key={u.entraObjectId} value={u.entraObjectId}>
                      {u.displayName || u.userPrincipalName}
                    </option>
                  ))}
                </select>
              </Labelled>
            </div>
          )}
        </fieldset>
      )}

      {closes && (
        <>
          <Labelled label="Closure reason" required>
            <input
              type="text"
              value={closureReason}
              onChange={e => setClosureReason(e.currentTarget.value)}
              placeholder="One line — why this stopped."
              style={inputStyle}
            />
          </Labelled>
          <Labelled label="Closure note" required={outcome === 'Salvage'}>
            <textarea
              value={closureNote}
              onChange={e => setClosureNote(e.currentTarget.value)}
              rows={3}
              placeholder={
                outcome === 'Salvage'
                  ? 'What was harvested, and where it now lives.'
                  : 'What was learned. Optional.'
              }
              style={textareaStyle}
            />
          </Labelled>
        </>
      )}

      {outcome === 'Park' && (
        <>
          <Labelled label="Blocked reason" required>
            <select
              value={blockedReasonCode}
              onChange={e => setBlockedReasonCode(e.currentTarget.value)}
              style={inputStyle}
            >
              <option value="">Select…</option>
              {(blockedReasons.status === 'success' ? blockedReasons.data : []).map(r => (
                <option key={r.blockedReasonCode} value={r.blockedReasonCode}>
                  {r.blockedReasonName}
                </option>
              ))}
            </select>
          </Labelled>
          <Labelled label="Blocked note">
            <input
              type="text"
              value={blockedNote}
              onChange={e => setBlockedNote(e.currentTarget.value)}
              style={inputStyle}
            />
          </Labelled>
        </>
      )}

      {blockingProblems.length > 0 && (
        <ul
          style={{
            margin: 0,
            paddingLeft: 18,
            fontSize: 12,
            color: tokens.statusAmber,
            display: 'grid',
            gap: 3,
          }}
        >
          {blockingProblems.map(p => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      )}

      {state.status === 'error' && <ErrorPanel error={state.error} maxWidth="100%" />}

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        {!confirming ? (
          <button
            type="button"
            disabled={blockingProblems.length > 0 || busy}
            onClick={() => setConfirming(true)}
            style={{
              ...primaryButtonStyle,
              opacity: blockingProblems.length > 0 ? 0.5 : 1,
              cursor: blockingProblems.length > 0 ? 'not-allowed' : 'pointer',
            }}
          >
            {OUTCOME_LABELS[outcome]}
          </button>
        ) : (
          <>
            <span style={{ fontSize: 13, color: tokens.ink2 }}>
              {overridden.size > 0
                ? 'This records an override on an append-only ledger. It cannot be edited afterwards — only compensated. Continue?'
                : 'This writes an entry to the append-only gate log with your name on it. Continue?'}
            </span>
            <button
              type="button"
              disabled={busy}
              onClick={() => void submit()}
              style={primaryButtonStyle}
            >
              {busy ? 'Recording…' : 'Yes, record it'}
            </button>
          </>
        )}
        <button
          type="button"
          disabled={busy}
          onClick={confirming ? () => setConfirming(false) : onCancel}
          style={secondaryButtonStyle}
        >
          Cancel
        </button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Bits
// ---------------------------------------------------------------------------

function SectionHeader({ label, trailing }: { label: string; trailing?: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
      <h2
        style={{
          fontSize: tokens.sizeH3,
          fontWeight: tokens.weightDemi,
          color: tokens.ink,
          margin: 0,
          fontFamily: tokens.fontDisplay,
        }}
      >
        {label}
      </h2>
      {trailing}
    </div>
  )
}

function CheckRow({
  checked,
  onChange,
  label,
}: {
  checked: boolean
  onChange: () => void
  label: string
}) {
  return (
    <label
      style={{
        display: 'grid',
        gridTemplateColumns: 'auto 1fr',
        gap: 10,
        alignItems: 'start',
        fontSize: 13,
        color: tokens.ink2,
        lineHeight: 1.45,
        padding: '4px 0',
        cursor: 'pointer',
      }}
    >
      <input type="checkbox" checked={checked} onChange={onChange} style={{ marginTop: 3 }} />
      <span>{label}</span>
    </label>
  )
}

function Labelled({
  label,
  required,
  children,
}: {
  label: string
  required?: boolean
  children: React.ReactNode
}) {
  return (
    <label style={{ display: 'grid', gap: 5 }}>
      <span
        style={{
          fontSize: 10.5,
          fontWeight: tokens.weightDemi,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: tokens.ink3,
        }}
      >
        {label}
        {required && <span style={{ color: tokens.gold, marginLeft: 4 }}>*</span>}
      </span>
      {children}
    </label>
  )
}

const inputStyle: React.CSSProperties = {
  padding: '9px 11px',
  fontSize: 13,
  fontFamily: tokens.fontDisplay,
  color: tokens.ink,
  background: tokens.paper,
  border: `1px solid ${tokens.line2}`,
  borderRadius: tokens.radiusMd,
  width: '100%',
  boxSizing: 'border-box',
}

const textareaStyle: React.CSSProperties = {
  ...inputStyle,
  resize: 'vertical',
  minHeight: 56,
}

const fieldsetStyle: React.CSSProperties = {
  border: `1px solid ${tokens.line2}`,
  borderRadius: tokens.radiusMd,
  padding: '10px 14px 12px',
  margin: 0,
}

const legendStyle: React.CSSProperties = {
  fontSize: 10.5,
  fontWeight: tokens.weightDemi,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  color: tokens.ink3,
  padding: '0 6px',
}

const primaryButtonStyle: React.CSSProperties = {
  background: tokens.gold,
  color: tokens.paper,
  border: 'none',
  borderRadius: tokens.radiusMd,
  padding: '10px 20px',
  fontFamily: tokens.fontDisplay,
  fontSize: 12,
  fontWeight: tokens.weightDemi,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  cursor: 'pointer',
}

const secondaryButtonStyle: React.CSSProperties = {
  background: 'transparent',
  color: tokens.ink2,
  border: `1px solid ${tokens.line2}`,
  borderRadius: tokens.radiusMd,
  padding: '10px 18px',
  fontFamily: tokens.fontDisplay,
  fontSize: 12,
  fontWeight: tokens.weightMedium,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  cursor: 'pointer',
}
